import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Gamepad2,
  Sparkles,
  Brain,
  Coins,
  Grid3X3,
  Gem,
  Swords,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ArcadeLeaderboard from "@/components/ArcadeLeaderboard";
import JewelsGame from "@/components/JewelsGame";
import FireworksGame from "@/components/FireworksGame";
import CrossoverShowdown from "@/components/CrossoverShowdown";
import DragoniousFightsEggNest from "@/components/DragoniousFightsEggNest";
import DragoniousCCG from "@/components/DragoniousCCG";
import DragoniousDFCL from "@/components/DragoniousDFCL";
import MikoArcadeBeatEmUp from "@/components/MikoArcadeBeatEmUp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "showdown",  label: "Showdown",  icon: <Swords size={14} /> },
  { key: "miko",      label: "🦉 Miko's Arcade", icon: <Gamepad2 size={14} /> },
  { key: "eggnest",   label: "🥚 Egg Nest", icon: <Sparkles size={14} /> },
  { key: "ccg",       label: "🃏 Cards",   icon: <Sparkles size={14} /> },
  // 🔥 Oct 2026 — DFCL is the card FIGHTING game; Cards is the gallery.
  // Two different rooms, each with its own purpose. Rules + duel UI live here.
  { key: "dfcl",      label: "🔥 Flame Card Legends", icon: <Swords size={14} /> },
  { key: "scratch",   label: "Scratch",   icon: <Sparkles size={14} /> },
  { key: "memory",    label: "Memory",    icon: <Brain size={14} /> },
  { key: "slots",     label: "Slots",     icon: <Coins size={14} /> },
  { key: "bingo",     label: "Bingo",     icon: <Grid3X3 size={14} /> },
  { key: "jewels",    label: "Jewels",    icon: <Gem size={14} /> },
  { key: "fireworks", label: "🎆 4th July", icon: <Sparkles size={14} /> },
];

export default function PhoenixArcade() {
  const { user } = useAuth();
  const [active, setActive] = useState("showdown");
  const [eggReady, setEggReady] = useState(false);

  // Poll the Egg Nest cooldown so the tab can pulse when a hatch is
  // available. Cheap request (~1 KB). Runs every 30s + on activation.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/dragonious-egg/state`, { withCredentials: true });
        if (!alive) return;
        setEggReady(!!r.data?.can_hatch);
      } catch (e) { /* silent — non-critical */ }
    };
    check();
    const id = setInterval(check, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, [user, active]);

  if (!user) {
    return (
      <section className="py-20 bg-[#0A0A0A] text-center">
        <Gamepad2 size={42} className="text-[#D4AF37] mx-auto mb-4" />
        <h2 className="font-display text-4xl text-white mb-2">Phoenix Arcade</h2>
        <p className="text-neutral-400 max-w-md mx-auto">
          Sign in to play the daily arcade.
        </p>
      </section>
    );
  }

  return (
    <section
      id="phoenix-arcade"
      data-testid="phoenix-arcade"
      className="py-16 md:py-24 bg-gradient-to-b from-[#0A0A0A] via-[#1a0d2b] to-[#0A0A0A] min-h-screen"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] mb-4">
            <Gamepad2 size={14} />
            <span className="font-accent text-xs tracking-[0.3em] uppercase">
              Phoenix Arcade
            </span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl text-white leading-[0.9] mb-3">
            Play. Earn. <span className="text-[#D4AF37]">Fly higher.</span>
          </h1>
          <p className="text-neutral-400 max-w-xl mx-auto text-sm">
            Ten mini-games. Server-fair RNG. Daily and weekly rewards in
            Phoenix Tokens 🐦‍🔥
          </p>
        </div>

        <div className="flex justify-center mb-6 flex-wrap gap-2">
          {TABS.map((t) => {
            const showPulse = t.key === "eggnest" && eggReady && active !== "eggnest";
            return (
              <button
                key={t.key}
                data-testid={`arcade-tab-${t.key}`}
                onClick={() => setActive(t.key)}
                className={`relative px-4 py-2.5 border-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors ${
                  active === t.key
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                    : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
                }`}
              >
                {t.icon}
                {t.label}
                {showPulse && (
                  <span
                    data-testid="egg-ready-pulse"
                    className="absolute -top-1.5 -right-1.5 flex h-3.5 w-3.5"
                    aria-label="Egg ready to hatch"
                  >
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-600 border border-red-300 shadow-[0_0_8px_rgba(239,68,68,0.9)]"></span>
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <ArcadeLeaderboard />

        <div className="bg-[#0F0F0F] border border-zinc-800 p-6 md:p-8">
          {active === "showdown" && <CrossoverShowdown />}
          {active === "miko" && <MikoArcadeBeatEmUp />}
          {active === "eggnest" && <DragoniousFightsEggNest />}
          {active === "ccg" && <DragoniousCCG onJumpToTab={setActive} />}
          {active === "dfcl" && <DragoniousDFCL onJumpToTab={setActive} />}
          {active === "scratch" && <ScratchGame />}
          {active === "memory" && <MemoryGame />}
          {active === "slots" && <SlotsGame />}
          {active === "bingo" && <BingoGame />}
          {active === "jewels" && <JewelsGame />}
          {active === "fireworks" && <FireworksGame />}
        </div>
      </div>
    </section>
  );
}

/* ============================ Scratch ============================ */
const SCRATCH_ICONS = {
  spark: "✨", ember: "🪙", flame: "🔥", blaze: "🟠",
  inferno: "💎", phoenix: "🐦‍🔥",
};

function ScratchGame() {
  const { refreshUser } = useAuth();
  const [state, setState] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/arcade/scratch/today`);
      setState(data);
      if (data.claimed) {
        setRevealed({ 0: true, 1: true, 2: true });
        setResult({ payout: data.outcome?.payout || 0, cells: data.cells });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load scratch card.");
    }
  };
  useEffect(() => { load(); }, []);

  const reveal = (i) => {
    if (state?.claimed) return;
    setRevealed((m) => ({ ...m, [i]: true }));
  };

  const allRevealed = state && [0, 1, 2].every((i) => revealed[i]);

  const claim = async () => {
    if (!allRevealed || state.claimed) return;
    setClaiming(true);
    try {
      const { data } = await axios.post(`${API}/arcade/scratch/claim`);
      setResult({ payout: data.outcome?.payout || 0, cells: data.cells });
      setState((s) => ({ ...s, claimed: true, outcome: data.outcome }));
      toast.success(`🎟️ +${((data.outcome?.payout || 0) * 100).toLocaleString()} Chits!`, { duration: 4000 });
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally {
      setClaiming(false);
    }
  };

  if (!state) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;

  return (
    <div data-testid="arcade-scratch" className="text-center">
      <h2 className="font-display text-3xl text-white mb-2">Daily Scratch Card 🎟️</h2>
      <p className="text-sm text-neutral-400 mb-6">
        Tap each cell to reveal. Match all 3 for big payouts. Refreshes daily at 00:00 PT.
      </p>
      <div className="grid grid-cols-3 gap-3 max-w-md mx-auto mb-6">
        {[0, 1, 2].map((i) => {
          const isOpen = !!revealed[i];
          const cellKey = state.cells?.[i];
          return (
            <button
              key={i}
              data-testid={`scratch-cell-${i}`}
              onClick={() => reveal(i)}
              disabled={isOpen || state.claimed}
              className={`aspect-square text-6xl flex items-center justify-center border-4 transition-all ${
                isOpen
                  ? "bg-[#D4AF37]/10 border-[#D4AF37]"
                  : "bg-gradient-to-br from-purple-700 to-red-700 border-[#D4AF37] hover:scale-105 cursor-pointer animate-pulse"
              }`}
            >
              {isOpen ? SCRATCH_ICONS[cellKey] || "?" : "?"}
            </button>
          );
        })}
      </div>
      {state.claimed ? (
        <div className="bg-[#1a0d2b] border-2 border-[#D4AF37] p-4 max-w-md mx-auto">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
            Today's Result
          </div>
          <div className="font-display text-3xl text-white mt-1">+{(((result?.payout || state.outcome?.payout) || 0) * 100).toLocaleString()} Chits</div>
          <p className="text-xs text-neutral-500 mt-2">Come back tomorrow for a fresh card.</p>
        </div>
      ) : (
        <button
          data-testid="scratch-claim"
          onClick={claim}
          disabled={!allRevealed || claiming}
          className="btn-omni btn-red disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {claiming ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {allRevealed ? "Claim Reward" : "Reveal all 3 cells"}
        </button>
      )}
    </div>
  );
}

/* ============================ Memory ============================ */
function MemoryGame() {
  const { refreshUser } = useAuth();
  const [cards, setCards] = useState([]);
  const [gameId, setGameId] = useState(null);
  const [flipped, setFlipped] = useState([]);     // indices currently face-up
  const [matched, setMatched] = useState({});     // index -> true
  const [attempts, setAttempts] = useState(0);
  const [startTs, setStartTs] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const lockRef = useRef(false);

  const start = async () => {
    setLoading(true);
    setFlipped([]);
    setMatched({});
    setAttempts(0);
    setCompleted(false);
    setLastResult(null);
    try {
      const { data } = await axios.post(`${API}/arcade/memory/start`);
      setCards(data.cards);
      setGameId(data.game_id);
      setStartTs(Date.now());
    } catch (e) {
      toast.error("Couldn't start game.");
    } finally {
      setLoading(false);
    }
  };

  const flip = (i) => {
    if (lockRef.current) return;
    if (matched[i] || flipped.includes(i)) return;
    const next = [...flipped, i];
    setFlipped(next);
    setAttempts((a) => a + 1);
    if (next.length === 2) {
      lockRef.current = true;
      const [a, b] = next;
      if (cards[a].sticker_id === cards[b].sticker_id) {
        // Match!
        setTimeout(() => {
          setMatched((m) => ({ ...m, [a]: true, [b]: true }));
          setFlipped([]);
          lockRef.current = false;
          // Check completion
          if (Object.keys({ ...matched, [a]: true, [b]: true }).length === cards.length) {
            finishGame();
          }
        }, 350);
      } else {
        setTimeout(() => {
          setFlipped([]);
          lockRef.current = false;
        }, 800);
      }
    }
  };

  const finishGame = async () => {
    if (completed) return;
    setCompleted(true);
    const timeSec = (Date.now() - startTs) / 1000;
    try {
      const { data } = await axios.post(`${API}/arcade/memory/complete`, {
        game_id: gameId,
        time_sec: Number(timeSec.toFixed(2)),
        attempts,
      });
      setLastResult(data);
      toast.success(`🧩 Cleared! +${data.payout} Phoenix Tokens`, { duration: 4500 });
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim payout.");
    }
  };

  return (
    <div data-testid="arcade-memory" className="text-center">
      <h2 className="font-display text-3xl text-white mb-2">Sticker Memory 🧩</h2>
      <p className="text-sm text-neutral-400 mb-5">
        Match 4 pairs. Fewer attempts + faster time = more Phoenix Tokens. Max 50 Phoenix Tokens.
      </p>
      <div className="flex items-center justify-center gap-4 text-xs mb-5">
        <span className="text-neutral-500">Attempts: <span className="text-[#D4AF37] font-bold">{attempts}</span></span>
        <span className="text-neutral-500">Pairs: <span className="text-[#D4AF37] font-bold">{Object.keys(matched).length / 2}/4</span></span>
      </div>
      {cards.length === 0 ? (
        <button
          data-testid="memory-start"
          onClick={start}
          disabled={loading}
          className="btn-omni btn-red disabled:opacity-40"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />}
          Start Game
        </button>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3 max-w-xl mx-auto mb-5">
            {cards.map((c, i) => {
              const isOpen = flipped.includes(i) || matched[i];
              return (
                <button
                  key={c.id}
                  data-testid={`memory-card-${i}`}
                  onClick={() => flip(i)}
                  disabled={isOpen || completed}
                  className={`aspect-square text-4xl md:text-5xl flex items-center justify-center border-2 transition-all ${
                    isOpen
                      ? matched[i]
                        ? "bg-emerald-900/30 border-emerald-600"
                        : "bg-[#D4AF37]/10 border-[#D4AF37]"
                      : "bg-gradient-to-br from-purple-800 to-purple-900 border-purple-600 hover:scale-105"
                  }`}
                >
                  {isOpen ? c.icon : "?"}
                </button>
              );
            })}
          </div>
          {completed && lastResult && (
            <div className="bg-[#1a0d2b] border-2 border-[#D4AF37] p-4 max-w-md mx-auto mb-4">
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">Cleared</div>
              <div className="font-display text-3xl text-white mt-1">+{lastResult.payout} Phoenix Tokens</div>
              <p className="text-xs text-neutral-500 mt-2">
                {lastResult.time_sec.toFixed(1)}s · {lastResult.attempts} attempts
              </p>
            </div>
          )}
          <button
            data-testid="memory-restart"
            onClick={start}
            disabled={loading}
            className="btn-omni btn-outline-gold disabled:opacity-40"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />}
            {completed ? "Play Again" : "Restart"}
          </button>
        </>
      )}
    </div>
  );
}

/* ============================ Slots ============================ */
function SlotsGame() {
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState(null);
  // 🎰 Feb 2026 — upgraded to 4 reels. Default array sized to match
  // the backend reel count the moment /state responds.
  const [reels, setReels] = useState(["✨", "✨", "✨", "✨"]);
  const [spinning, setSpinning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/arcade/slots/state`).then(({ data }) => {
      setState(data);
      const n = Number(data?.reel_count) || 4;
      setReels(Array(n).fill(data?.symbols?.[0]?.icon || "✨"));
    });
  }, [user?.tokens]);

  const reelCount = state?.reel_count || 4;

  const spin = async () => {
    if (spinning) return;
    if (!state || (user?.tokens || 0) < state.cost) {
      toast.error(`Need ${state?.cost || 5} Phoenix Tokens to spin.`);
      return;
    }
    setSpinning(true);
    setLastResult(null);
    const tickInterval = setInterval(() => {
      const sym = state.symbols;
      setReels(Array.from({ length: reelCount }, () => sym[Math.floor(Math.random() * sym.length)].icon));
    }, 80);
    try {
      const { data } = await axios.post(`${API}/arcade/slots/spin`);
      setTimeout(() => {
        clearInterval(tickInterval);
        setReels(data.reels.map((r) => r.icon));
        setLastResult(data);
        if (data.payout > 0) {
          toast.success(`🎰 ${data.outcome.toUpperCase()} — +${data.payout} Phoenix Tokens`, { duration: 5000 });
        }
        refreshUser?.();
        setSpinning(false);
      }, 1200);
    } catch (e) {
      clearInterval(tickInterval);
      toast.error(e?.response?.data?.detail || "Spin failed.");
      setSpinning(false);
    }
  };

  // Top quad payout to advertise on the strapline.
  const topQuad = state?.quad_payout ? Math.max(...Object.values(state.quad_payout)) : 1000;

  return (
    <div data-testid="arcade-slots" className="text-center">
      <h2 className="font-display text-3xl text-white mb-2">Phoenix Slots 🎰</h2>
      <p className="text-sm text-neutral-400 mb-5">
        {state?.cost || 5} Phoenix Tokens/spin · {reelCount} reels · payouts up to {topQuad} Phoenix Tokens on a 4-of-a-kind phoenix.
      </p>
      <div className="bg-gradient-to-b from-yellow-900/20 to-red-900/20 border-4 border-[#D4AF37] p-5 sm:p-6 max-w-xl mx-auto mb-5">
        <div
          className="grid gap-2 sm:gap-3"
          style={{ gridTemplateColumns: `repeat(${reelCount}, minmax(0, 1fr))` }}
        >
          {reels.map((icon, i) => (
            <div
              key={i}
              data-testid={`slots-reel-${i}`}
              className={`aspect-square bg-black border-2 border-[#D4AF37]/60 flex items-center justify-center text-5xl sm:text-6xl ${
                spinning ? "animate-pulse" : ""
              }`}
            >
              {icon}
            </div>
          ))}
        </div>
      </div>
      {lastResult && (
        <div className="text-sm text-neutral-300 mb-4">
          {lastResult.outcome === "quad"   && <span className="text-amber-300 font-black animate-pulse">🐦‍🔥 QUAD! +{lastResult.payout} Phoenix Tokens</span>}
          {lastResult.outcome === "triple" && <span className="text-[#D4AF37] font-bold">🎉 TRIPLE +{lastResult.payout} Phoenix Tokens</span>}
          {lastResult.outcome === "pair"   && <span className="text-purple-400 font-bold">PAIR +{lastResult.payout} Phoenix Tokens</span>}
          {lastResult.outcome === "miss"   && <span className="text-neutral-500">No match — -{lastResult.cost} Phoenix Tokens</span>}
        </div>
      )}
      <button
        data-testid="slots-spin"
        onClick={spin}
        disabled={spinning || !state}
        className="btn-omni btn-red disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {spinning ? <Loader2 size={14} className="animate-spin" /> : <Coins size={14} />}
        {spinning ? "Spinning..." : `Spin · ${state?.cost || 5} Phoenix Tokens`}
      </button>
      <p className="text-[10px] text-neutral-600 uppercase tracking-widest mt-4">
        Balance: <span className="text-[#D4AF37]">{user?.tokens || 0} Phoenix Tokens</span>
      </p>
    </div>
  );
}

/* ============================ Bingo ============================ */
function BingoGame() {
  const { refreshUser } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/arcade/bingo/card`);
      setCard(data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const claim = async () => {
    setClaiming(true);
    try {
      const { data } = await axios.post(`${API}/arcade/bingo/claim`);
      if (data.total_payout > 0) {
        toast.success(`🎉 +${data.total_payout} Phoenix Tokens! ${data.newly_cleared} line${data.newly_cleared !== 1 ? "s" : ""} + ${data.full_bonus ? "BLACKOUT" : ""}`, { duration: 5000 });
      } else {
        toast("No new lines yet — keep playing!", { duration: 2500 });
      }
      refreshUser?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally {
      setClaiming(false);
    }
  };

  if (loading || !card) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;

  const grid = card.grid || [];
  const marks = card.marks || [];

  return (
    <div data-testid="arcade-bingo" className="text-center">
      <h2 className="font-display text-3xl text-white mb-2">Weekly Bingo 🎟️</h2>
      <p className="text-sm text-neutral-400 mb-5">
        Do real activities → squares auto-mark. Clear a row/col/diag → +{card.row_bonus} Phoenix Tokens. Blackout → +{card.full_bonus} Phoenix Tokens. Resets Mon 00:00 PT.
      </p>
      <div className="flex items-center justify-center gap-4 text-xs mb-5">
        <span className="text-neutral-500">Lines cleared: <span className="text-[#D4AF37] font-bold">{card.lines_cleared}</span></span>
        <span className="text-neutral-500">Marks: <span className="text-[#D4AF37] font-bold">{marks.filter(Boolean).length}/25</span></span>
        {card.full_claimed && <span className="text-[#D4AF37] font-bold">BLACKOUT ✓</span>}
      </div>
      {/* Total Phoenix mega-bonus banner — clear all 6 vitals tiles in one PT week */}
      {card.vitals_mega_claimed ? (
        <div
          data-testid="bingo-mega-claimed"
          className="max-w-2xl mx-auto mb-5 p-3 border-2 border-[#D4AF37] bg-gradient-to-r from-[#D4AF37]/10 via-[#DC2626]/10 to-[#4C1D95]/10 flex items-center justify-center gap-2"
        >
          <span className="text-xl">🐦‍🔥</span>
          <span className="font-display text-base text-[#D4AF37] tracking-wider uppercase">
            Total Phoenix · +{card.vitals_mega_bonus} Phoenix Tokens claimed this week
          </span>
        </div>
      ) : (card.vitals_mega_tiles_hit ?? 0) >= 5 ? (
        <div
          data-testid="bingo-mega-ready"
          className="max-w-2xl mx-auto mb-5 p-3 border-2 border-emerald-500 bg-emerald-900/30 flex items-center justify-center gap-2 animate-pulse"
        >
          <span className="text-xl">🐦‍🔥</span>
          <span className="font-display text-base text-emerald-200 tracking-wider uppercase">
            1 vital tile from Total Phoenix · +{card.vitals_mega_bonus} Phoenix Tokens awaits
          </span>
        </div>
      ) : (card.vitals_mega_tiles_hit ?? 0) >= 1 ? (
        <div
          data-testid="bingo-mega-progress"
          className="max-w-2xl mx-auto mb-5 p-2.5 border border-[#D4AF37]/40 bg-[#D4AF37]/5 flex items-center justify-center gap-2"
        >
          <span>🐦‍🔥</span>
          <span className="text-xs text-[#D4AF37] uppercase tracking-widest font-bold">
            Total Phoenix progress · {card.vitals_mega_tiles_hit}/{card.vitals_mega_tiles_total ?? 6} vitals tiles
          </span>
        </div>
      ) : (
        <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 mb-5">
          🐦‍🔥 Clear all 6 Vitals tiles this week → bonus +{card.vitals_mega_bonus ?? 250} Phoenix Tokens
        </p>
      )}
      <div className="grid grid-cols-5 gap-1.5 max-w-2xl mx-auto mb-5">
        {grid.map((cell, i) => {
          const isMarked = marks[i];
          const isFree = cell.is_free;
          return (
            <div
              key={i}
              data-testid={`bingo-cell-${i}`}
              className={`aspect-square p-1 flex flex-col items-center justify-center text-[9px] sm:text-[10px] uppercase tracking-tight font-bold border-2 transition-all ${
                isMarked
                  ? isFree
                    ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                    : "border-emerald-600 bg-emerald-900/30 text-white"
                  : "border-zinc-800 bg-[#0A0A0A] text-neutral-500"
              }`}
            >
              <div className="text-xl sm:text-2xl">{cell.icon}</div>
              <div className="leading-tight mt-0.5 px-0.5 text-center break-words">
                {cell.label}
              </div>
            </div>
          );
        })}
      </div>
      <button
        data-testid="bingo-claim"
        onClick={claim}
        disabled={claiming}
        className="btn-omni btn-red disabled:opacity-40"
      >
        {claiming ? <Loader2 size={14} className="animate-spin" /> : <Grid3X3 size={14} />}
        Claim Rewards
      </button>
    </div>
  );
}
